import React, { useMemo, useState } from "react";
import classNames from "classnames";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  listTickets,
  createTicket,
  changeTicketStatus,
} from "../../services/crm/clementino-service";
import {
  TicketClementino,
  normalizeStatus,
} from "../../models/crm/clementino/clementino-types";
import { money, toFloat } from "../../pages/expeditions/components/envois-shared";

// Customer complaints console, shared by the Expéditions and Réservations modules.
// Each module passes its own config (categories, dossier reference label, seed data).

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

export type ReclamStatus = "Nouvelle" | "En cours" | "En attente client" | "Résolue" | "Rejetée";
export type ReclamPriority = "Basse" | "Normale" | "Haute" | "Urgente";
export type CompensationType = "Aucune" | "Remboursement" | "Avoir" | "Geste commercial" | "Nouvelle livraison / nouveau billet";

export interface Reclamation {
  id: string;
  code: string;
  createdAt: string; // ISO datetime
  createdBy: string;
  client: string;
  phone: string;
  reference: string; // dossier concerned (shipment code, ticket…)
  category: string;
  priority: ReclamPriority;
  channel: string;
  status: ReclamStatus;
  assignee: string;
  subject: string;
  description: string;
  amountClaimed: number;
  compensation: { type: CompensationType; amount: number };
  dueAt: string; // SLA deadline ISO
  resolvedAt?: string;
  history: [string, string, string][]; // [stamp, text, author]
}

export interface ReclamConfig {
  title: string;
  subtitle: string;
  referenceLabel: string;
  referencePlaceholder: string;
  categories: string[];
  agents: string[];
  // Known dossiers, offered as suggestions when creating a complaint.
  references: { code: string; label: string; client: string; phone: string }[];
  seed: Reclamation[];
}

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

export const STATUSES: ReclamStatus[] = ["Nouvelle", "En cours", "En attente client", "Résolue", "Rejetée"];
const PRIORITIES: ReclamPriority[] = ["Urgente", "Haute", "Normale", "Basse"];
const CHANNELS = ["WhatsApp", "Appel", "Agence", "Email", "Portail client"];
const COMPENSATIONS: CompensationType[] = ["Aucune", "Remboursement", "Avoir", "Geste commercial", "Nouvelle livraison / nouveau billet"];

// Resolution deadline per priority, in hours.
export const SLA_HOURS: Record<ReclamPriority, number> = { Urgente: 4, Haute: 24, Normale: 48, Basse: 72 };

const STATUS_STYLE: Record<ReclamStatus, { bg: string; fg: string; dot: string }> = {
  Nouvelle: { bg: "#e0f2fe", fg: "#0369a1", dot: "#0ea5e9" },
  "En cours": { bg: "#ede9fe", fg: "#5b21b6", dot: "#7c3aed" },
  "En attente client": { bg: "#fef3c7", fg: "#b45309", dot: "#f59e0b" },
  Résolue: { bg: "#d1fae5", fg: "#047857", dot: "#10b981" },
  Rejetée: { bg: "#f1f5f9", fg: "#64748b", dot: "#94a3b8" },
};

const PRIORITY_STYLE: Record<ReclamPriority, { bg: string; fg: string }> = {
  Urgente: { bg: "#fee2e2", fg: "#b91c1c" },
  Haute: { bg: "#ffedd5", fg: "#c2410c" },
  Normale: { bg: "#f1f5f9", fg: "#475569" },
  Basse: { bg: "#f8fafc", fg: "#94a3b8" },
};


const pad = (n: number) => String(n).padStart(2, "0");
const isoNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const addHours = (iso: string, h: number) => {
  const d = new Date(new Date(iso).getTime() + h * 3600000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const stamp = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}`;
const fDateTime = (iso: string) =>
  new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const isOpen = (r: Reclamation) => r.status !== "Résolue" && r.status !== "Rejetée";
const plural = (n: number, word: string, tail = "") => `${n} ${word}${n > 1 ? "s" : ""}${tail ? ` ${tail}` : ""}`;

// "dans 5 h" / "en retard de 2 h" for open complaints.
function slaInfo(r: Reclamation): { label: string; late: boolean; soon: boolean } {
  if (!isOpen(r)) return { label: r.resolvedAt ? `Clôturée le ${fDateTime(r.resolvedAt)}` : "Clôturée", late: false, soon: false };
  const diffH = (new Date(r.dueAt).getTime() - Date.now()) / 3600000;
  const fmt = (h: number) => (Math.abs(h) >= 48 ? `${Math.round(Math.abs(h) / 24)} j` : `${Math.max(1, Math.round(Math.abs(h)))} h`);
  return diffH < 0 ? { label: `En retard de ${fmt(diffH)}`, late: true, soon: false } : { label: `Échéance dans ${fmt(diffH)}`, late: false, soon: diffH < 6 };
}

/* -------------------------------------------------------------------------- */
/*                                  Console                                   */
/* -------------------------------------------------------------------------- */

const mapClementinoStatusToReclam = (st?: any): ReclamStatus => {
  const s = normalizeStatus(st).toUpperCase();
  if (!s) return "Nouvelle";
  if (s.includes("OPEN") || s === "NOUVEAU") return "Nouvelle";
  if (s.includes("PROGRESS") || s === "EN_COURS") return "En cours";
  if (s.includes("ATTENTE") || s === "PENDING") return "En attente client";
  if (s.includes("RESOL") || s === "RESOLU") return "Résolue";
  if (s.includes("CLOSE") || s.includes("REJET") || s === "FERME") return "Rejetée";
  return "En cours";
};

const mapReclamStatusToClementino = (st: ReclamStatus): string => {
  switch (st) {
    case "Nouvelle":
      return "OPENED";
    case "En cours":
      return "IN_PROGRESS";
    case "En attente client":
      return "WAITING_CLIENT";
    case "Résolue":
      return "RESOLVED";
    case "Rejetée":
      return "CLOSED";
    default:
      return "IN_PROGRESS";
  }
};

const clementinoTicketToReclamation = (t: TicketClementino): Reclamation => {
  const dateIso = t.dateCreation || t.createdAt || new Date().toISOString();
  const rawStatus = (t as any).status ?? (t as any).statut;
  return {
    id: String(t.id),
    code: `REC-${String(t.id).padStart(4, "0")}`,
    createdAt: dateIso,
    createdBy: "Service Client",
    client: t.nomPlaignant || "Client",
    phone: t.telPlaignant || "—",
    reference: t.numLt || (t as any).numLettreTransport || (t as any).numExpedition || "—",
    category: t.type === "INCIDENT" ? "Colis endommagé" : "Retard de livraison",
    priority: "Normale",
    channel: "WhatsApp",
    status: mapClementinoStatusToReclam(rawStatus),
    assignee: "Service Client",
    subject: t.sujet,
    description: t.description,
    amountClaimed: 0,
    compensation: { type: "Aucune", amount: 0 },
    dueAt: new Date(new Date(dateIso).getTime() + 48 * 3600000).toISOString(),
    history: [],
  };
};

export default function ReclamationsConsole({ config, agent = "yasmine" }: { config: ReclamConfig; agent?: string }) {
  const isExpedition = config.referenceLabel.toLowerCase().includes("expédition");
  const [list, setList] = useState<Reclamation[]>(() => (isExpedition ? [] : config.seed));
  const [seq, setSeq] = useState(() => (isExpedition ? 0 : config.seed.length));
  const [loading, setLoading] = useState(isExpedition);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = React.useCallback(async () => {
    if (!isExpedition) return;
    setLoading(true);
    setError(null);
    try {
      const tickets = await listTickets();
      setList(tickets.map(clementinoTicketToReclamation));
    } catch (err: any) {
      setError(
        err?.message ||
        "Impossible de charger les réclamations depuis Clementino. Vérifiez votre clé d API Gateway."
      );
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [isExpedition]);

  React.useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);
  const [tab, setTab] = useState<"Toutes" | ReclamStatus>("Toutes");
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("Toutes");
  const [fPrio, setFPrio] = useState("Toutes");
  const [fChannel, setFChannel] = useState("Tous");
  const [fAssignee, setFAssignee] = useState("Tous");
  const [onlyLate, setOnlyLate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2400);
  };

  const update = async (id: string, patch: Partial<Reclamation>, event?: string) => {
    if (isExpedition && patch.status) {
      try {
        await changeTicketStatus(id, mapReclamStatusToClementino(patch.status));
      } catch (err: any) {
        flash("Erreur lors de la mise à jour sur Clementino: " + (err?.message || "Erreur serveur"));
      }
    }
    setList((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              ...patch,
              history: event ? [...r.history, [stamp(isoNow()), event, agent]] : r.history,
            }
          : r
      )
    );
  };

  const filtered = list
    .filter((r) => {
      if (tab !== "Toutes" && r.status !== tab) return false;
      if (fCat !== "Toutes" && r.category !== fCat) return false;
      if (fPrio !== "Toutes" && r.priority !== fPrio) return false;
      if (fChannel !== "Tous" && r.channel !== fChannel) return false;
      if (fAssignee !== "Tous" && r.assignee !== fAssignee) return false;
      if (onlyLate && !slaInfo(r).late) return false;
      if (q.trim()) {
        const hay = [r.code, r.client, r.phone, r.reference, r.subject, r.category, r.assignee].join(" ").toLowerCase();
        if (!hay.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    })
    // Open & late first, then by priority, then newest.
    .sort((a, b) => {
      const la = slaInfo(a).late ? 0 : 1;
      const lb = slaInfo(b).late ? 0 : 1;
      if (isOpen(a) !== isOpen(b)) return isOpen(a) ? -1 : 1;
      if (la !== lb) return la - lb;
      const pa = PRIORITIES.indexOf(a.priority);
      const pb = PRIORITIES.indexOf(b.priority);
      return pa !== pb ? pa - pb : b.createdAt.localeCompare(a.createdAt);
    });

  const open = list.filter(isOpen);
  const late = open.filter((r) => slaInfo(r).late);
  const closed = list.filter((r) => r.status === "Résolue" && r.resolvedAt);
  const avgHours = closed.length ? closed.reduce((a, r) => a + (new Date(r.resolvedAt!).getTime() - new Date(r.createdAt).getTime()) / 3600000, 0) / closed.length : 0;
  const resolvedRate = list.length ? Math.round((list.filter((r) => r.status === "Résolue").length / list.length) * 100) : 0;
  const compensations = list.filter((r) => r.status === "Résolue").reduce((a, r) => a + toFloat(r.compensation.amount), 0);

  const detail = detailId ? list.find((r) => r.id === detailId) : undefined;
  const selectCls = "h-9 px-2.5 border border-[#e2e8f0] rounded-lg bg-white text-xs text-[#1f1b2e]";

  return (
    <div className="p-6 lg:p-7 min-h-full flex flex-col bg-[#faf7ff] text-[#1f1b2e] overflow-clip">
      {/* Header — pinned under the app header */}
      <div className="sticky top-[50px] z-30 -mx-6 px-6 lg:-mx-7 lg:px-7 -mt-6 lg:-mt-7 pt-6 lg:pt-7 pb-3 mb-4 bg-[#faf7ff] flex items-start justify-between gap-x-6 gap-y-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{config.title}</h1>
          <p className="text-xs text-[#6b6480] mt-1 font-medium">
            {config.subtitle} · {open.length} ouverte{open.length > 1 ? "s" : ""} · {late.length} hors délai
          </p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          {isExpedition && (
            <button
              type="button"
              onClick={fetchTickets}
              disabled={loading}
              className="flex items-center gap-1.5 h-9 px-3.5 border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-900 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              <ArrowPathIcon className={classNames("w-3.5 h-3.5", loading && "animate-spin")} />
              <span>Actualiser Clementino</span>
            </button>
          )}
          <button type="button" onClick={() => flash(`Export Excel généré (${filtered.length} réclamations)`)} className="flex items-center gap-1.5 h-9 px-3.5 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-emerald-600" />
            Export Excel
          </button>
          <button type="button" onClick={() => setCreating(true)} className="h-9 px-4 bg-[#6d28d9] hover:bg-purple-800 rounded-lg text-xs font-bold text-white whitespace-nowrap">
            + Nouvelle réclamation
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Kpi label="Ouvertes" value={String(open.length)} sub={plural(open.filter((r) => r.status === "Nouvelle").length, "nouvelle", "à qualifier")} color="#0369a1" />
        <Kpi label="Hors délai" value={String(late.length)} sub="échéance de traitement dépassée" color="#b91c1c" onClick={() => setOnlyLate((v) => !v)} active={onlyLate} />
        <Kpi label="Taux de résolution" value={`${resolvedRate} %`} sub={plural(list.filter((r) => r.status === "Résolue").length, "résolue")} color="#047857" />
        <Kpi label="Délai moyen" value={avgHours ? `${avgHours >= 48 ? Math.round(avgHours / 24) + " j" : Math.round(avgHours) + " h"}` : "—"} sub="de la création à la résolution" color="#5b21b6" />
        <Kpi label="Indemnisations" value={money(compensations)} sub="accordées sur les dossiers résolus" color="#b45309" />
      </div>

      {/* Status tabs */}
      <div className="flex p-[3px] bg-[#f1ecfe] rounded-[10px] gap-[3px] mb-3 overflow-x-auto self-start max-w-full">
        {(["Toutes", ...STATUSES] as const).map((s) => {
          const n = s === "Toutes" ? list.length : list.filter((r) => r.status === s).length;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setTab(s)}
              className={classNames("px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-1.5", tab === s ? "bg-white text-[#4c1d95] shadow-xs" : "text-[#7c729b] hover:text-[#4c1d95]")}
            >
              {s !== "Toutes" && <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_STYLE[s].dot }} />}
              {s}
              <span className="px-1.5 rounded-full bg-[#ede9fe] text-[10px] text-[#5b21b6]">{n}</span>
            </button>
          );
        })}
      </div>

      {/* Search & filters */}
      <div className="flex items-center gap-2.5 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Code, client, téléphone, ${config.referenceLabel.toLowerCase()}, objet…`} className="w-full h-9 pl-9 pr-3 border border-[#ddd6fe] rounded-lg bg-white text-xs focus:outline-none focus:ring-2 focus:ring-purple-300" />
        </div>
        <select value={fCat} onChange={(e) => setFCat(e.target.value)} className={selectCls}>
          <option value="Toutes">Toutes catégories</option>
          {config.categories.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select value={fPrio} onChange={(e) => setFPrio(e.target.value)} className={selectCls}>
          <option value="Toutes">Toutes priorités</option>
          {PRIORITIES.map((p) => (
            <option key={p}>{p}</option>
          ))}
        </select>
        <select value={fChannel} onChange={(e) => setFChannel(e.target.value)} className={selectCls}>
          <option value="Tous">Tous canaux</option>
          {CHANNELS.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select value={fAssignee} onChange={(e) => setFAssignee(e.target.value)} className={selectCls}>
          <option value="Tous">Tous les agents</option>
          {config.agents.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </select>
        <label className={classNames("h-9 px-3 flex items-center gap-2 rounded-lg border text-xs font-semibold cursor-pointer whitespace-nowrap", onlyLate ? "border-rose-300 bg-rose-50 text-rose-700" : "border-[#e2e8f0] bg-white text-gray-600")}>
          <input type="checkbox" checked={onlyLate} onChange={() => setOnlyLate(!onlyLate)} className="accent-rose-600" />
          Hors délai uniquement
        </label>
      </div>

      {error && (
        <div className="mb-4 p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 shrink-0" />
            <span>{error}</span>
          </div>
          <button
            type="button"
            onClick={fetchTickets}
            className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg text-xs cursor-pointer"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#ece7f8] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px] border-collapse text-xs">
            <thead>
              <tr className="bg-[#f6f3fd] text-left text-[10.5px] font-bold tracking-[0.07em] uppercase text-[#6b6480]">
                {["Réclamation", "Client", config.referenceLabel, "Catégorie & objet", "Priorité", "Statut", "Délai de traitement", "Assignée à", "Réclamé / accordé"].map((h) => (
                  <th key={h} className="px-3.5 py-3 whitespace-nowrap border-b border-[#ece7f8]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const S = STATUS_STYLE[r.status];
                const P = PRIORITY_STYLE[r.priority];
                const sla = slaInfo(r);
                return (
                  <tr key={r.id} onClick={() => setDetailId(r.id)} className="border-b border-[#f4f1fb] align-top hover:bg-[#faf8ff] cursor-pointer">
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <span className="font-mono font-semibold text-[#5b21b6] bg-[#ede9fe] px-2 py-0.5 rounded">{r.code}</span>
                      <div className="text-[11px] text-gray-500 mt-1">
                        {fDateTime(r.createdAt)} · {r.channel}
                      </div>
                    </td>
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-gray-900">{r.client}</div>
                      <div className="font-mono text-[11px] text-gray-500">{r.phone}</div>
                    </td>
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <span className="font-mono text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">{r.reference || "—"}</span>
                    </td>
                    <td className="px-3.5 py-3 max-w-[280px]">
                      <div className="font-semibold text-gray-900">{r.category}</div>
                      <div className="text-[11px] text-gray-500 truncate" title={r.subject}>
                        {r.subject}
                      </div>
                    </td>
                    <td className="px-3.5 py-3">
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold whitespace-nowrap" style={{ background: P.bg, color: P.fg }}>
                        {r.priority}
                      </span>
                    </td>
                    <td className="px-3.5 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold whitespace-nowrap" style={{ background: S.bg, color: S.fg }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: S.dot }} />
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <span className={classNames("inline-flex items-center gap-1 text-[11px] font-semibold", sla.late ? "text-rose-700" : sla.soon ? "text-amber-700" : isOpen(r) ? "text-gray-600" : "text-gray-400")}>
                        {sla.late ? <ExclamationTriangleIcon className="w-3.5 h-3.5" /> : <ClockIcon className="w-3.5 h-3.5" />}
                        {sla.label}
                      </span>
                    </td>
                    <td className="px-3.5 py-3 whitespace-nowrap">{r.assignee || <span className="text-gray-400">Non assignée</span>}</td>
                    <td className="px-3.5 py-3 whitespace-nowrap text-right">
                      <div className="font-semibold text-gray-900">{r.amountClaimed ? money(r.amountClaimed) : "—"}</div>
                      <div className="text-[11px] text-gray-500">{r.compensation.type === "Aucune" ? "aucune indemnisation" : `${r.compensation.type} · ${money(r.compensation.amount)}`}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="p-8 text-center text-xs text-gray-500">Aucune réclamation ne correspond à ces critères.</div>}
        <div className="px-4 py-2.5 bg-[#fbf9ff] border-t border-[#ece7f8] text-xs text-gray-600">
          {filtered.length} réclamation{filtered.length > 1 ? "s" : ""} affichée{filtered.length > 1 ? "s" : ""} · tri : ouvertes et hors délai d'abord, puis par priorité
        </div>
      </div>

      {detail && <ReclamationDrawer r={detail} config={config} agent={agent} onClose={() => setDetailId(null)} update={update} flash={flash} />}

      {creating && (
        <NewReclamationModal
          config={config}
          onClose={() => setCreating(false)}
          onCreate={async (data) => {
            if (isExpedition) {
              try {
                const ticket = await createTicket({
                  type: "RECLAMATION",
                  sujet: data.subject,
                  description: data.description,
                  numLt: data.reference || undefined,
                  nomPlaignant: data.client,
                  telPlaignant: data.phone,
                });
                const newRec = clementinoTicketToReclamation(ticket);
                setList((prev) => [newRec, ...prev]);
                setCreating(false);
                setDetailId(newRec.id);
                flash(`Réclamation ${newRec.code} enregistrée sur Clementino · client notifié`);
              } catch (err: any) {
                flash("Erreur lors de la création du ticket Clementino: " + (err?.message || "Erreur serveur"));
              }
            } else {
              const n = seq + 1;
              const now = isoNow();
              const code = `REC-${now.slice(8, 10)}${now.slice(5, 7)}-${String(n).padStart(4, "0")}`;
              const item: Reclamation = {
                ...data,
                id: `rc${Date.now()}`,
                code,
                createdAt: now,
                createdBy: agent,
                status: "Nouvelle",
                assignee: "",
                compensation: { type: "Aucune", amount: 0 },
                dueAt: addHours(now, SLA_HOURS[data.priority]),
                history: [[stamp(now), `Réclamation enregistrée (${data.channel})`, agent]],
              };
              setList((prev) => [item, ...prev]);
              setSeq(n);
              setCreating(false);
              setDetailId(item.id);
              flash(`Réclamation ${code} créée — accusé de réception envoyé au client`);
            }
          }}
        />
      )}

      {toast && <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-[95] px-4 py-2.5 bg-[#1f1b2e] rounded-full text-xs font-semibold text-white shadow-xl">{toast}</div>}
    </div>
  );
}

function Kpi({ label, value, sub, color, onClick, active }: { label: string; value: string; sub: string; color: string; onClick?: () => void; active?: boolean }) {
  return (
    <div
      onClick={onClick}
      className={classNames("p-3 bg-white border rounded-xl", onClick && "cursor-pointer hover:border-rose-200", active ? "border-rose-300 ring-2 ring-rose-100" : "border-[#ece7f8]")}
      title={onClick ? "Cliquer pour filtrer" : undefined}
    >
      <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">{label}</div>
      <div className="text-xl font-bold mt-1" style={{ color }}>
        {value}
      </div>
      <div className="text-[11px] text-[#9b93b3]">{sub}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Detail drawer                               */
/* -------------------------------------------------------------------------- */

function ReclamationDrawer({
  r,
  config,
  agent,
  onClose,
  update,
  flash,
}: {
  r: Reclamation;
  config: ReclamConfig;
  agent: string;
  onClose: () => void;
  update: (id: string, patch: Partial<Reclamation>, event?: string) => void;
  flash: (m: string) => void;
}) {
  const [reply, setReply] = useState("");
  const [replyChannel, setReplyChannel] = useState("WhatsApp");
  const [note, setNote] = useState("");
  const [compType, setCompType] = useState<CompensationType>(r.compensation.type);
  const [compAmount, setCompAmount] = useState(r.compensation.amount ? String(r.compensation.amount) : "");
  const [closing, setClosing] = useState<null | "Résolue" | "Rejetée">(null);
  const [closeNote, setCloseNote] = useState("");
  const S = STATUS_STYLE[r.status];
  const P = PRIORITY_STYLE[r.priority];
  const sla = slaInfo(r);
  const open = isOpen(r);
  const sectionTitle = "text-[11px] font-bold tracking-[0.07em] uppercase text-[#8b81a8]";
  const inputCls = "w-full h-9 px-2.5 border border-[#e2e8f0] rounded-lg bg-white text-xs";

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-[rgba(31,27,46,0.35)]" onMouseDown={onClose}>
      <div onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-[560px] h-full bg-[#faf7ff] shadow-2xl flex flex-col text-xs text-[#1f1b2e]">
        <div className="px-5 py-4 bg-white border-b border-[#ece7f8] flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-bold text-[#5b21b6] bg-[#ede9fe] px-2 py-0.5 rounded">{r.code}</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded font-bold" style={{ background: S.bg, color: S.fg }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: S.dot }} />
                {r.status}
              </span>
              <span className="px-2 py-0.5 rounded font-bold" style={{ background: P.bg, color: P.fg }}>
                {r.priority}
              </span>
            </div>
            <div className="text-base font-bold mt-2">{r.subject}</div>
            <div className={classNames("mt-1 inline-flex items-center gap-1 font-semibold", sla.late ? "text-rose-700" : sla.soon ? "text-amber-700" : "text-gray-500")}>
              <ClockIcon className="w-3.5 h-3.5" />
              {sla.label} {open && `(échéance ${fDateTime(r.dueAt)})`}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500" title="Fermer">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 p-4 bg-white border border-[#ece7f8] rounded-xl">
            <Field label="Client" value={r.client} />
            <Field label="Téléphone" value={r.phone} mono />
            <Field label={config.referenceLabel} value={r.reference || "—"} mono />
            <Field label="Catégorie" value={r.category} />
            <Field label="Canal" value={r.channel} />
            <Field label="Créée" value={`${fDateTime(r.createdAt)} par ${r.createdBy}`} />
            <Field label="Montant réclamé" value={r.amountClaimed ? money(r.amountClaimed) : "—"} />
            <Field label="Indemnisation" value={r.compensation.type === "Aucune" ? "Aucune" : `${r.compensation.type} · ${money(r.compensation.amount)}`} />
            <div className="col-span-2">
              <div className="text-[11px] text-[#8b81a8]">Description du client</div>
              <div className="mt-0.5 leading-relaxed whitespace-pre-line">{r.description}</div>
            </div>
          </div>

          {open && (
            <div className="p-4 bg-white border border-[#ece7f8] rounded-xl space-y-3">
              <div className={sectionTitle}>Traitement</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-600 mb-1">Assignée à</span>
                  <select
                    value={r.assignee}
                    onChange={(e) => {
                      update(r.id, { assignee: e.target.value, status: r.status === "Nouvelle" && e.target.value ? "En cours" : r.status }, e.target.value ? `Assignée à ${e.target.value}` : "Désassignée");
                    }}
                    className={inputCls}
                  >
                    <option value="">Non assignée</option>
                    {config.agents.map((a) => (
                      <option key={a}>{a}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-600 mb-1">Statut</span>
                  <select value={r.status} onChange={(e) => update(r.id, { status: e.target.value as ReclamStatus }, `Statut : ${r.status} → ${e.target.value}`)} className={inputCls}>
                    {STATUSES.filter((s) => s !== "Résolue" && s !== "Rejetée").map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-600 mb-1">Priorité</span>
                  <select
                    value={r.priority}
                    onChange={(e) => {
                      const p = e.target.value as ReclamPriority;
                      update(r.id, { priority: p, dueAt: addHours(r.createdAt, SLA_HOURS[p]) }, `Priorité : ${r.priority} → ${p}`);
                    }}
                    className={inputCls}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-[1fr_130px_auto] gap-2 items-end">
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-600 mb-1">Indemnisation proposée</span>
                  <select value={compType} onChange={(e) => setCompType(e.target.value as CompensationType)} className={inputCls}>
                    {COMPENSATIONS.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[11px] font-semibold text-gray-600 mb-1">Montant (FCFA)</span>
                  <input type="number" min={0} value={compAmount} onChange={(e) => setCompAmount(e.target.value)} disabled={compType === "Aucune"} className={classNames(inputCls, "disabled:bg-gray-50")} />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const amount = compType === "Aucune" ? 0 : Number(compAmount) || 0;
                    if (compType !== "Aucune" && amount <= 0 && compType !== "Nouvelle livraison / nouveau billet") return flash("Indique le montant de l'indemnisation.");
                    update(r.id, { compensation: { type: compType, amount } }, compType === "Aucune" ? "Indemnisation retirée" : `Indemnisation proposée : ${compType}${amount ? ` · ${money(amount)}` : ""}`);
                    flash("Indemnisation enregistrée");
                  }}
                  className="h-9 px-3 bg-[#ede9fe] hover:bg-[#ddd6fe] rounded-lg font-semibold text-[#5b21b6] whitespace-nowrap"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          )}

          <div className="p-4 bg-white border border-[#ece7f8] rounded-xl space-y-2.5">
            <div className={sectionTitle}>Répondre au client</div>
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder={`Bonjour ${r.client.split(" ")[0]}, nous avons bien reçu votre réclamation…`} className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg text-xs resize-y" />
            <div className="flex items-center gap-2">
              <select value={replyChannel} onChange={(e) => setReplyChannel(e.target.value)} className="h-9 px-2.5 border border-[#e2e8f0] rounded-lg bg-white text-xs">
                {["WhatsApp", "SMS", "Email", "Appel (compte rendu)"].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={!reply.trim()}
                onClick={() => {
                  update(r.id, { status: r.status === "Nouvelle" ? "En cours" : r.status }, `Réponse envoyée par ${replyChannel} : « ${reply.trim()} »`);
                  setReply("");
                  flash(`Réponse envoyée au client par ${replyChannel}`);
                }}
                className="ml-auto h-9 px-4 bg-[#059669] disabled:bg-emerald-200 rounded-lg font-bold text-white"
              >
                Envoyer
              </button>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-[#f4f1fb]">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note interne (non visible par le client)" className={inputCls} />
              <button
                type="button"
                disabled={!note.trim()}
                onClick={() => {
                  update(r.id, {}, `Note interne : ${note.trim()}`);
                  setNote("");
                }}
                className="h-9 px-3 border border-[#e2e8f0] bg-white disabled:text-gray-300 rounded-lg font-semibold whitespace-nowrap"
              >
                Ajouter
              </button>
            </div>
          </div>

          <div className="p-4 bg-white border border-[#ece7f8] rounded-xl">
            <div className={sectionTitle}>Historique</div>
            <div className="mt-2">
              {r.history
                .slice()
                .reverse()
                .map((h, i) => (
                  <div key={i} className="grid grid-cols-[76px_12px_minmax(0,1fr)] gap-2 pt-2.5">
                    <span className="font-mono text-[11px] text-gray-500 text-right">{h[0]}</span>
                    <span className="w-2 h-2 mt-1 rounded-full" style={{ background: i === 0 ? "#6d28d9" : "#cbc4dc" }} />
                    <span className="leading-snug">
                      {h[1]} <span className="text-gray-400">— {h[2]}</span>
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 bg-white border-t border-[#ece7f8]">
          {!open ? (
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Dossier clôturé.</span>
              <button type="button" onClick={() => update(r.id, { status: "En cours", resolvedAt: undefined, dueAt: addHours(isoNow(), SLA_HOURS[r.priority]) }, "Réclamation rouverte")} className="ml-auto h-9 px-4 border border-[#ddd6fe] bg-white rounded-lg font-semibold text-[#4c1d95]">
                Rouvrir
              </button>
            </div>
          ) : closing ? (
            <div className="space-y-2">
              <textarea
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
                rows={2}
                placeholder={closing === "Résolue" ? "Solution apportée au client (obligatoire)" : "Motif du rejet (obligatoire)"}
                className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg text-xs"
              />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setClosing(null)} className="h-9 px-4 border border-[#e2e8f0] bg-white rounded-lg font-semibold">
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={!closeNote.trim()}
                  onClick={() => {
                    update(r.id, { status: closing, resolvedAt: isoNow() }, `${closing === "Résolue" ? "Résolue" : "Rejetée"} : ${closeNote.trim()}`);
                    flash(closing === "Résolue" ? "Réclamation résolue — le client est notifié" : "Réclamation rejetée — le client est notifié");
                    setClosing(null);
                    setCloseNote("");
                  }}
                  className={classNames("h-9 px-4 rounded-lg font-bold text-white disabled:opacity-40", closing === "Résolue" ? "bg-[#16a34a]" : "bg-[#64748b]")}
                >
                  Confirmer
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setClosing("Rejetée")} className="h-9 px-4 border border-[#e2e8f0] bg-white rounded-lg font-semibold text-gray-600">
                Rejeter
              </button>
              <button type="button" onClick={() => setClosing("Résolue")} className="h-9 px-4 bg-[#16a34a] hover:bg-green-700 rounded-lg font-bold text-white">
                Marquer résolue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] text-[#8b81a8]">{label}</div>
      <div className={classNames("mt-0.5 font-semibold truncate", mono && "font-mono")} title={value}>
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Creation modal                               */
/* -------------------------------------------------------------------------- */

type NewData = Pick<Reclamation, "client" | "phone" | "reference" | "category" | "priority" | "channel" | "subject" | "description" | "amountClaimed">;

function NewReclamationModal({ config, onClose, onCreate }: { config: ReclamConfig; onClose: () => void; onCreate: (d: NewData) => void }) {
  const [d, setD] = useState<NewData>({ client: "", phone: "", reference: "", category: config.categories[0], priority: "Normale", channel: "WhatsApp", subject: "", description: "", amountClaimed: 0 });
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<NewData>) => setD((x) => ({ ...x, ...p }));
  const listId = useMemo(() => `refs-${Math.random().toString(36).slice(2)}`, []);
  const inputCls = "w-full h-9 px-2.5 border border-[#e2e8f0] rounded-lg bg-white text-xs";
  const labelCls = "block text-[11px] font-semibold text-gray-600 mb-1";

  // Picking a known dossier pre-fills the client.
  const onReference = (v: string) => {
    const known = config.references.find((x) => x.code === v);
    set(known && !d.client ? { reference: v, client: known.client, phone: known.phone } : { reference: v });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const missing = [!d.client.trim() && "client", !d.phone.trim() && "téléphone", !d.subject.trim() && "objet", !d.description.trim() && "description"].filter(Boolean);
    if (missing.length) return setError(`Champs obligatoires manquants : ${missing.join(", ")}.`);
    onCreate({ ...d, client: d.client.trim(), phone: d.phone.trim(), subject: d.subject.trim(), description: d.description.trim() });
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-[rgba(31,27,46,0.5)]">
      <form onSubmit={submit} className="w-full max-w-[620px] max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl text-xs text-[#1f1b2e] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#ece7f8] flex items-center">
          <div className="flex-1">
            <div className="text-base font-bold">Nouvelle réclamation</div>
            <div className="text-gray-500 mt-0.5">Un accusé de réception avec le numéro de réclamation est envoyé au client.</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className={labelCls}>{config.referenceLabel}</span>
            <input list={listId} value={d.reference} onChange={(e) => onReference(e.target.value)} placeholder={config.referencePlaceholder} className={classNames(inputCls, "font-mono")} />
            <datalist id={listId}>
              {config.references.map((x) => (
                <option key={x.code} value={x.code}>
                  {x.label}
                </option>
              ))}
            </datalist>
          </label>
          <label className="block">
            <span className={labelCls}>Client *</span>
            <input value={d.client} onChange={(e) => set({ client: e.target.value })} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Téléphone *</span>
            <input value={d.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+237 6XX XX XX XX" className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Catégorie</span>
            <select value={d.category} onChange={(e) => set({ category: e.target.value })} className={inputCls}>
              {config.categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Canal de réception</span>
            <select value={d.channel} onChange={(e) => set({ channel: e.target.value })} className={inputCls}>
              {CHANNELS.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <div className="col-span-2">
            <span className={labelCls}>Priorité · délai de traitement</span>
            <div className="grid grid-cols-4 gap-2">
              {PRIORITIES.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => set({ priority: p })}
                  className={classNames("h-10 rounded-lg border-[1.5px] font-semibold", d.priority === p ? "border-[#6d28d9] bg-[#f5f1ff] text-[#4c1d95]" : "border-[#e2e8f0] text-gray-600")}
                >
                  {p}
                  <span className="block text-[10px] font-normal text-gray-500">{SLA_HOURS[p]} h</span>
                </button>
              ))}
            </div>
          </div>
          <label className="block col-span-2">
            <span className={labelCls}>Objet *</span>
            <input value={d.subject} onChange={(e) => set({ subject: e.target.value })} placeholder="Résumé en une phrase" className={inputCls} />
          </label>
          <label className="block col-span-2">
            <span className={labelCls}>Description *</span>
            <textarea value={d.description} onChange={(e) => set({ description: e.target.value })} rows={4} placeholder="Ce que le client signale, faits, dates, lieux…" className="w-full px-2.5 py-2 border border-[#e2e8f0] rounded-lg text-xs resize-y" />
          </label>
          <label className="block">
            <span className={labelCls}>Montant réclamé (FCFA)</span>
            <input type="number" min={0} value={d.amountClaimed || ""} onChange={(e) => set({ amountClaimed: Number(e.target.value) || 0 })} placeholder="0" className={inputCls} />
          </label>
          {error && <div className="col-span-2 p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 font-semibold">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-[#ece7f8] bg-gray-50 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-4 border border-gray-300 bg-white rounded-lg font-semibold">
            Annuler
          </button>
          <button type="submit" className="h-9 px-4 bg-[#6d28d9] hover:bg-purple-800 rounded-lg font-bold text-white">
            Enregistrer la réclamation
          </button>
        </div>
      </form>
    </div>
  );
}
